'use client'
import React, { useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { useBlogCategories, useBlogs } from '@/app/services/blog'
import { getCategoryName } from '@/app/utils/blog'
import NewsSkeleton from '@/app/components/skeleton/NewsSkeleton'
import ArticleError from './ArticleError'
import NoArticles from './NoArticles'
import { BlogCard, FeaturedBlogCard } from '../BlogCard'

export default function BlogsPage() {
  const t = useTranslations('Blog')
  const language = useLocale()
  const listRef = useRef<HTMLDivElement>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const { data: categories } = useBlogCategories()
  const { data, meta, isFetching, isError } = useBlogs({
    page,
    page_size: 16,
    language,
    sort: 'desc',
    ...(selectedCategory ? { category_id: selectedCategory } : {}),
  })

  const labels = { minRead: t('min_read'), readMore: t('read_more') }
  const lastPage = meta?.last_page || 1
  const showFeatured = page === 1 && data.length > 0
  const featured = showFeatured ? data[0] : null
  const rest = showFeatured ? data.slice(1) : data

  const goToPage = (newPage: number) => {
    setPage(newPage)
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const selectCategory = (id: string | null) => {
    setSelectedCategory(id)
    setPage(1)
  }

  return (
    <>
      {/* Header */}
      <section className="px-6 pt-14 md:pt-20 pb-10 relative overflow-hidden">
        <svg
          className="hidden md:block absolute right-6 top-8 w-72 h-40 opacity-70"
          viewBox="0 0 260 160"
          fill="none"
          aria-hidden="true">
          <path d="M0 120 C 80 20, 160 160, 250 40" stroke="#A7D3AF" strokeWidth="2" strokeDasharray="6 6" />
        </svg>
        <div className="relative max-w-3xl">
          <span className="border-2 border-dashed border-[#059669] text-[#059669] bg-primary-light font-bold px-4 py-1 rounded-lg inline-block mb-6">
            {t('eyebrow')}
          </span>
          <h1 className="text-[#064E3B] text-4xl md:text-6xl font-bold leading-tight mb-5">
            {t('title')}{' '}
            <span className="bg-[#B91C1C] text-white px-3 py-1 rounded-lg rotate-[-3deg] inline-block">{t('title_highlight')}</span>
          </h1>
          <p className="text-[#064E3B]/80 text-lg md:text-xl leading-relaxed max-w-2xl">{t('subtitle')}</p>
        </div>
      </section>

      <section ref={listRef} className="px-4 md:px-10 pb-20 scroll-mt-6">
        {/* Categories */}
        {categories?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10 px-2">
            <CategoryPill label={t('all')} active={!selectedCategory} onClick={() => selectCategory(null)} />
            {categories.map(cat => (
              <CategoryPill
                key={cat?.id}
                label={getCategoryName(cat, language)}
                active={selectedCategory == cat?.id}
                onClick={() => selectCategory(cat?.id)}
              />
            ))}
          </div>
        )}

        {isFetching ? (
          <>
            <NewsSkeleton />
            <NewsSkeleton />
          </>
        ) : (
          <>
            {isError ? (
              <ArticleError errorMessage={t('error_text')} displayButton={false} />
            ) : (
              <>
                {data?.length <= 0 ? (
                  <NoArticles />
                ) : (
                  <>
                    {featured && (
                      <div className="mb-10">
                        <FeaturedBlogCard blog={featured} locale={language} labels={labels} />
                      </div>
                    )}

                    {rest.length > 0 && (
                      <>
                        {showFeatured && (
                          <h2 className="text-[#064E3B] text-2xl md:text-3xl font-bold mb-6 px-2">{t('latest')}</h2>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {rest.map(post => (
                            <BlogCard key={post?.id} blog={post} locale={language} labels={labels} />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Pagination */}
                    {lastPage > 1 && (
                      <nav className="flex items-center justify-center mt-14 gap-4" aria-label="Pagination">
                        <button
                          onClick={() => goToPage(page - 1)}
                          aria-label={t('previous')}
                          className="w-12 h-12 rounded-full bg-white border-2 border-[#A7D3AF] text-[#064E3B] flex items-center justify-center hover:bg-[#A7D3AF] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          disabled={page <= 1}>
                          <ChevronLeft size={22} />
                        </button>

                        <div className="flex gap-2">
                          {Array.from({ length: lastPage }, (_, i) => {
                            const pageNumber = i + 1
                            const active = pageNumber === (meta?.current_page || page)
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => goToPage(pageNumber)}
                                aria-label={t('page_of', { page: pageNumber, total: lastPage })}
                                aria-current={active ? 'page' : undefined}
                                className={`w-10 h-10 rounded-full font-bold transition-colors cursor-pointer ${
                                  active ? 'bg-[#FFE37B] text-[#064E3B]' : 'bg-white text-[#064E3B]/60 hover:text-[#064E3B]'
                                }`}>
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>

                        <button
                          onClick={() => goToPage(page + 1)}
                          aria-label={t('next')}
                          className="w-12 h-12 rounded-full bg-white border-2 border-[#A7D3AF] text-[#064E3B] flex items-center justify-center hover:bg-[#A7D3AF] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          disabled={page >= lastPage}>
                          <ChevronRight size={22} />
                        </button>
                      </nav>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </section>
    </>
  )
}

function CategoryPill({ label, active = false, onClick }: { label: string; active?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-5 py-2 rounded-full text-sm font-bold transition-colors cursor-pointer border-2 ${
        active
          ? 'bg-primary border-[#008D63] text-white'
          : 'bg-white border-[#A7D3AF] text-[#064E3B] hover:bg-[#AFD4A940]'
      }`}>
      {label}
    </button>
  )
}
